import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, CircularProgress, Alert, Stack,
  Autocomplete, useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { exportService } from '../../services/accounting/export.service';
import { serverMessage } from '../../utils/apiConvert';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const CustomerAdvancesDebtors = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [totals, setTotals] = useState(null);

  // Filters (as of today: a wallet balance cannot be shown for a past date)
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    financeReportsService.getCustomers(user.typingCenterId || user.typing_center_id).then(setCustomers).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeReportsService.getCustomerAdvancesDebtors({ customerId: selectedCustomer?.id });
      // Sort by largest debit first
      setData((res?.rows || []).map((r) => ({ ...r, id: r.customerId, credit: Number(r.credit), debit: Number(r.debit) }))
        .sort((a, b) => b.debit - a.debit));
      setTotals(res);
    } catch (err) {
      setData([]);
      setTotals(null);
      setError(serverMessage(err, 'Failed to load customer advances & debtors report'));
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export with the page's own filters (BE-14b); the file comes back under the server's name
  const [exporting, setExporting] = useState(null);
  const doExport = async (format) => {
    setExporting(format);
    try {
      await exportService.download('customer-advances-debtors', format, { customerId: selectedCustomer?.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalCredit = Number(totals?.totalCredit || 0);
  const totalDebit = Number(totals?.totalDebit || 0);
  const netBalance = totalCredit - totalDebit;

  // ── Summary Cards ──
  const renderSummaryCards = () => {
    const cards = [
      { label: 'Total Advances (CR)', value: formatCurrency(totalCredit), color: isDark ? '#4caf50' : '#2e7d32', bg: isDark ? 'rgba(76,175,80,0.12)' : '#e8f5e9' },
      { label: 'Total Debtors (DR)', value: formatCurrency(totalDebit), color: isDark ? '#ef5350' : '#c62828', bg: isDark ? 'rgba(239,83,80,0.12)' : '#ffebee' },
      { label: 'Net Balance', value: formatCurrency(Math.abs(netBalance)), color: netBalance >= 0 ? (isDark ? '#4caf50' : '#2e7d32') : (isDark ? '#ef5350' : '#c62828'), bg: netBalance >= 0 ? (isDark ? 'rgba(76,175,80,0.12)' : '#e8f5e9') : (isDark ? 'rgba(239,83,80,0.12)' : '#ffebee') },
    ];
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {cards.map((c, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ bgcolor: c.bg, height: '100%' }}>
              <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 0.5 }}>
                  {c.label}
                </Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: c.color }}>
                  {c.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            Customer Advances & Debtors
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Client-wise credit and debit balances, as of today
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" startIcon={<ExcelIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('csv')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button size="small" variant="contained" startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('pdf')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button size="small" variant="outlined" onClick={fetchData}
            sx={{ minWidth: 36 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small" options={customers}
              value={selectedCustomer}
              onChange={(e, v) => { setSelectedCustomer(v); setPage(0); }}
              renderInput={(params) => <TextField {...params} label="Customer / Organization" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {!loading && renderSummaryCards()}

      {/* Table / Cards */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : paginatedData.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No records found</Typography></Paper>
          ) : paginatedData.map((row, idx) => {
            const net = row.credit - row.debit;
            return (
              <Card key={row.id} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                    <Box>
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{row.customerName}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>#{page * rowsPerPage + idx + 1}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Credit (Advance)</Typography>
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: row.credit > 0 ? '#4caf50' : 'text.secondary' }}>
                        {row.credit > 0 ? formatCurrency(row.credit) : '-'}
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Debit (Outstanding)</Typography>
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: row.debit > 0 ? '#f44336' : 'text.secondary' }}>
                        {row.debit > 0 ? formatCurrency(row.debit) : '-'}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Net</Typography>
                      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: net >= 0 ? '#4caf50' : '#f44336' }}>
                        {formatCurrency(Math.abs(net))} {net >= 0 ? 'CR' : 'DR'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          {/* Mobile Summary */}
          {!loading && data.length > 0 && (
            <Card sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, mb: 1 }}>Totals</Typography>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total Credit</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#4caf50' }}>{formatCurrency(totalCredit)}</Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total Debit</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#f44336' }}>{formatCurrency(totalDebit)}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Net</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: netBalance >= 0 ? '#4caf50' : '#f44336' }}>
                      {formatCurrency(Math.abs(netBalance))} {netBalance >= 0 ? 'CR' : 'DR'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', width: 40 }}>#</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Customer Name</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: '#4caf50' }}>Credit (Advance)</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: '#f44336' }}>Debit (Outstanding)</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Net Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No records found</Typography></TableCell></TableRow>
                ) : (
                  <>
                    {paginatedData.map((row, idx) => {
                      const net = row.credit - row.debit;
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{page * rowsPerPage + idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '13px', py: 1 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{row.customerName}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 600, color: row.credit > 0 ? '#4caf50' : 'text.secondary' }}>
                            {row.credit > 0 ? formatCurrency(row.credit) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 600, color: row.debit > 0 ? '#f44336' : 'text.secondary' }}>
                            {row.debit > 0 ? formatCurrency(row.debit) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 700, color: net >= 0 ? '#4caf50' : '#f44336' }}>
                            {formatCurrency(Math.abs(net))} {net >= 0 ? 'CR' : 'DR'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Summary Row */}
                    <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
                      <TableCell colSpan={2} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5 }}>
                        TOTALS ({data.length} customers)
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: '#4caf50' }}>
                        {formatCurrency(totalCredit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: '#f44336' }}>
                        {formatCurrency(totalDebit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: netBalance >= 0 ? '#4caf50' : '#f44336' }}>
                        {formatCurrency(Math.abs(netBalance))} {netBalance >= 0 ? 'CR' : 'DR'}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default CustomerAdvancesDebtors;
